// ============================================================================
// JARVISTRAVEL - TRAVEL PREFERENCES API
// The durable, model-steering travel preferences set in Settings. Model-ready:
// a live session reads/writes the backend `/auth/preferences` endpoint; a demo
// session persists locally so choices survive a reload (the client blocks demo
// writes from hitting the network, so we mirror them to localStorage here).
// ============================================================================

import { api, isDemoSession } from './client';
import type { UserPreferences } from '@/app/types';
import { reportApiFailure } from './failure';

/** The travel-planning slice of UserPreferences surfaced in Settings. */
export type TravelPreferences = Pick<
  UserPreferences,
  'dietaryRestrictions' | 'diningStyle' | 'interests' | 'localVsTourist' | 'tripIntensity' | 'budgetTier'
>;

export const DEFAULT_TRAVEL_PREFERENCES: TravelPreferences = {
  dietaryRestrictions: [],
  diningStyle: 'balanced',
  interests: [],
  localVsTourist: 'balanced',
  tripIntensity: 'balanced',
  budgetTier: 'moderate',
};

const DEMO_KEY = 'jarvis_travel_preferences';

function readDemo(): TravelPreferences {
  try {
    const raw = localStorage.getItem(DEMO_KEY);
    return raw ? { ...DEFAULT_TRAVEL_PREFERENCES, ...JSON.parse(raw) } : { ...DEFAULT_TRAVEL_PREFERENCES };
  } catch {
    return { ...DEFAULT_TRAVEL_PREFERENCES };
  }
}

function writeDemo(patch: Partial<TravelPreferences>): TravelPreferences {
  const next = { ...readDemo(), ...patch };
  try {
    localStorage.setItem(DEMO_KEY, JSON.stringify(next));
  } catch {
    /* storage may be unavailable — preference is best-effort */
  }
  return next;
}

/**
 * Remove the demo's locally-stored travel preferences, so they don't outlive the
 * demo for the next visitor (JAR-1992). Best-effort: clearing demo data must not
 * throw.
 */
export function clearDemoPreferences(): void {
  try {
    localStorage.removeItem(DEMO_KEY);
  } catch {
    /* storage unavailable — nothing to clear */
  }
}

// ---------------------------------------------------------------------------
// Backend contract — GET/POST /auth/preferences (snake_case envelope)
// ---------------------------------------------------------------------------

/** Backend user-preferences record. POST is a FULL-RECORD replace (Go zero
 *  values for anything omitted), so fields the FE doesn't surface here —
 *  notifications, display units, pace details — must be echoed back from the
 *  last GET or a save would silently reset them. */
interface BackendPreferences {
  id?: string;
  user_id?: string;
  notifications_email?: boolean;
  notifications_push?: boolean;
  display_currency?: string;
  display_temperature_unit?: string;
  dietary_restrictions?: string[];
  preferred_cuisines?: string[];
  spice_tolerance?: string;
  preferred_pace?: string;
  daily_start_time?: string;
  max_items_per_day?: number;
  interests?: string[];
  kid_friendly?: boolean;
  budget_tier?: string;
  local_vs_tourist?: string;
  dining_style?: string;
}

interface PreferencesEnvelope {
  success: boolean;
  message?: string;
  preferences?: BackendPreferences;
}

/** Backend enum fields arrive as free strings — accept only known values so a
 *  foreign value degrades to the FE default instead of breaking the pickers. */
const pick = <T extends string>(v: string | undefined, allowed: readonly T[]): T | undefined =>
  allowed.includes(v as T) ? (v as T) : undefined;

function fromBackend(p: BackendPreferences): Partial<TravelPreferences> {
  const out: Partial<TravelPreferences> = {};
  if (Array.isArray(p.dietary_restrictions)) out.dietaryRestrictions = p.dietary_restrictions;
  if (Array.isArray(p.interests)) out.interests = p.interests;
  const local = pick(p.local_vs_tourist, ['local', 'balanced', 'tourist'] as const);
  if (local) out.localVsTourist = local;
  const pace = pick(p.preferred_pace, ['chill', 'balanced', 'packed'] as const);
  if (pace) out.tripIntensity = pace;
  const tier = pick(p.budget_tier, ['budget', 'moderate', 'indulgent'] as const);
  if (tier) out.budgetTier = tier;
  const dining = pick(p.dining_style, ['adventurous', 'balanced', 'comfort'] as const);
  if (dining) out.diningStyle = dining;
  return out;
}

function toBackend(full: TravelPreferences, base: BackendPreferences): BackendPreferences {
  return {
    ...base, // echo unmanaged fields — POST replaces the whole record
    dietary_restrictions: full.dietaryRestrictions,
    interests: full.interests,
    local_vs_tourist: full.localVsTourist,
    preferred_pace: full.tripIntensity,
    budget_tier: full.budgetTier,
    dining_style: full.diningStyle,
  };
}

// The latest full backend record, kept so a save can echo the fields the FE
// doesn't manage. Populated by getPreferences (and refreshed by each save).
let lastBackendRecord: BackendPreferences | null = null;

/** Drop the cached backend record. Called on every auth identity change
 *  (sign-in, sign-up, sign-out) — a save must never echo the PREVIOUS
 *  user's record into the new user's preferences. */
export function clearPreferences(): void {
  lastBackendRecord = null;
}

/**
 * Read the backend record and cache it. REJECTS if it cannot be read.
 *
 * This is the ONE place that decides whether we hold the user's record
 * (JAR-2267). It used to return DEFAULT_TRAVEL_PREFERENCES on failure, which
 * made a failed read indistinguishable from a user who had set nothing — and
 * the save path then wrote those defaults over the stored row. A user with no
 * stored row is NOT this case: core answers sql.ErrNoRows with success and a
 * full default record (services/user-auth/internal/server/preferences.go), so
 * a rejection here is always a genuine transport or backend error.
 *
 * Both callers go through it, so the rule is stated once and returns a record
 * rather than a maybe-record: there is no second guard to fall out of step
 * with, and no branch that cannot be reached.
 */
async function loadBackendRecord(): Promise<BackendPreferences> {
  const result = await api.get<PreferencesEnvelope>('/auth/preferences');
  if (result.error || !result.data?.success || !result.data.preferences) {
    reportApiFailure('[preferences] get failed');
    throw new Error('Your travel preferences could not be loaded.');
  }
  lastBackendRecord = result.data.preferences;
  return lastBackendRecord;
}

/** Load the user's travel preferences. Rejects if they cannot be read. */
export async function getPreferences(): Promise<TravelPreferences> {
  if (isDemoSession()) return readDemo();
  const record = await loadBackendRecord();
  return { ...DEFAULT_TRAVEL_PREFERENCES, ...fromBackend(record) };
}

/**
 * Persist a partial update. Returns the merged preferences. Rejects on a live
 * save failure so the caller can surface it — the optimistic UI update is not a
 * substitute for knowing the write actually landed.
 */
export async function savePreferences(patch: Partial<TravelPreferences>): Promise<TravelPreferences> {
  if (isDemoSession()) return writeDemo(patch);
  // The POST replaces the whole backend record, so a save needs a record to
  // echo — and reads one rather than inventing it (JAR-2267). The old code fell
  // back to `lastBackendRecord ?? {}`, and that empty object is what reset every
  // column core upserts: the omitted fields arrive as Go zero values, so
  // notifications flip off, currency and temperature unit blank, and cuisines,
  // start time, max items and kid_friendly are cleared. If the record cannot be
  // read, this rejects and nothing is written at all.
  const base = lastBackendRecord ?? (await loadBackendRecord());
  const current = { ...DEFAULT_TRAVEL_PREFERENCES, ...fromBackend(base) };
  const merged = { ...current, ...patch };
  const result = await api.post<PreferencesEnvelope>('/auth/preferences', toBackend(merged, base));
  if (result.error || result.data?.success === false) {
    reportApiFailure('[preferences] save failed');
    throw new Error(result.error?.message || result.data?.message || 'Failed to save preferences');
  }
  if (result.data?.preferences) lastBackendRecord = result.data.preferences;
  return merged;
}
